import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
var _excluded = ["className", "options", "value", "canUseKeyboard", "style", "onSelect", "innerRef", "name", "isRequired"];
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Option } from '../';
import { appendHtmlClass, throttle, getAnimationDuration, getAnimationEasing } from '../../js/util';
import './_index.scss';
var OptionListMemo = /*#__PURE__*/React.memo(function OptionList(_ref) {
  var className = _ref.className,
    options = _ref.options,
    value = _ref.value,
    canUseKeyboard = _ref.canUseKeyboard,
    style = _ref.style,
    onSelect = _ref.onSelect,
    innerRef = _ref.innerRef,
    name = _ref.name,
    isRequired = _ref.isRequired,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  var keyThrottleMs = 500;
  var isValueValid = useCallback(function () {
    return value !== null && typeof value === 'string';
  }, [value]);
  var onSelectOption = useCallback(function (id, event) {
    return typeof onSelect === 'function' && onSelect(id, event);
  }, [onSelect]);
  var defaultFocusedOption = useMemo(function () {
    if (isValueValid()) return value;
    if (options.length > 0) return options[0].id;
    return null;
  }, [isValueValid, options, value]);
  var _useState = useState(defaultFocusedOption),
    focusedOption = _useState[0],
    setFocusedOption = _useState[1];
  var onFocus = useCallback(function (id) {
    return setFocusedOption(id);
  }, [setFocusedOption]);
  var onBlur = useCallback(function (id) {
    return setFocusedOption(null);
  }, [setFocusedOption]);
  useEffect(function () {
    var moveFocus = function moveFocus(direction) {
      if (focusedOption === null) return false;
      var index = options.findIndex(function (i) {
        return i.id === focusedOption;
      });
      if (index < 0) return false;
      index += direction ? -1 : 1;
      if (index < 0 || index > options.length - 1) return false;
      setFocusedOption(options[index].id);
    };
    var onKeyPress = function onKeyPress(event) {
      if (!canUseKeyboard) return false;
      event.preventDefault();
      var arrow = null;
      if (event.keyCode === 38) arrow = true; // arrow up
      else if (event.keyCode === 40) arrow = false; // arrow down
      if (arrow !== null) moveFocus(arrow);
    };
    var keyDownFn = throttle(onKeyPress, keyThrottleMs);
    document.addEventListener('keydown', keyDownFn);
    return function () {
      return document.removeEventListener('keydown', keyDownFn);
    };
  }, [options, focusedOption, setFocusedOption, canUseKeyboard]);
  useEffect(function () {
    var onKeyUp = function onKeyUp(event) {
      if (!canUseKeyboard) return false;
      event.preventDefault();
      if (event.keyCode === 13 && focusedOption !== null) {
        // enter key

        var option = options.find(function (i) {
          return i.id === focusedOption;
        });
        if (option) onSelectOption(option.id);
      }
    };
    var keyUpFn = throttle(onKeyUp, keyThrottleMs);
    document.addEventListener('keyup', keyUpFn);
    return function () {
      return document.removeEventListener('keyup', keyUpFn);
    };
  }, [onSelect, canUseKeyboard, focusedOption, options, onSelectOption]);
  var animationProps = useMemo(function () {
    var visible = {
      scaleY: 1,
      opacity: 1,
      transition: {
        duration: getAnimationDuration('OPTION_LIST_ANIMATION_DURATION'),
        ease: getAnimationEasing('ease')
      }
    };
    var hidden = {
      scaleY: 0,
      opacity: 0,
      transition: {
        staggerChildren: 0.03,
        duration: getAnimationDuration('OPTION_LIST_ANIMATION_DURATION'),
        ease: getAnimationEasing('ease')
      }
    };
    var targetStyle = typeof style === 'object' ? _objectSpread({}, style) : {};
    targetStyle = Object.assign({
      originY: 0
    }, targetStyle);
    return {
      isAnimated: true,
      variants: {
        visible: visible,
        hidden: hidden
      },
      animate: 'visible',
      initial: 'hidden',
      style: targetStyle
    };
  }, [style]);
  var newClassName = useMemo(function () {
    return appendHtmlClass(className, 'option-list');
  }, [className]);
  return /*#__PURE__*/React.createElement(Box, Object.assign({
    as: "ul",
    className: newClassName,
    ref: innerRef
  }, animationProps, rest), options.map(function (i) {
    return /*#__PURE__*/React.createElement(Option, {
      data: i,
      isSelected: value === i.id,
      isFocused: focusedOption === i.id,
      onSelect: onSelectOption,
      onFocus: onFocus,
      onBlur: onBlur,
      key: i.id
    });
  }), /*#__PURE__*/React.createElement("input", {
    type: "hidden",
    name: name,
    value: value
  }));
});
OptionListMemo.defaultProps = {
  canUseKeyboard: false,
  isRequired: false
};
var OptionListWithRef = /*#__PURE__*/React.forwardRef(function (props, ref) {
  return /*#__PURE__*/React.createElement(OptionListMemo, Object.assign({
    innerRef: ref
  }, props));
});
export default OptionListWithRef;